/**
 * Декларативное описание WebMCP-инструментов сайта (без функций исполнения).
 * Используется в двух местах:
 * 1. Статичный <script type="application/mcp+json"> в SSR HTML — виден
 *    краулерам/сканерам, не исполняющим JS.
 * 2. Runtime-регистрация через navigator.modelContext (webmcp-tools.tsx).
 */

const MC_SUMMARY_OUTPUT = {
  type: 'object',
  properties: {
    wordId: { type: 'string', description: 'Слаг мастер-класса' },
    name: { type: 'string', description: 'Название' },
    priceRub: { type: 'number', description: 'Цена в рублях' },
    rating: {
      type: ['number', 'null'],
      description: 'Средний рейтинг 1–5',
    },
    reviewsCount: { type: 'number', description: 'Число отзывов' },
    url: { type: 'string', description: 'Ссылка на страницу мастер-класса' },
  },
  required: ['wordId', 'name', 'priceRub', 'url'],
} as const;

export const WEBMCP_TOOL_DEFINITIONS = [
  {
    name: 'search_masterclasses',
    description:
      'Поиск мастер-классов по вязанию игрушек амигуруми по названию или тегам. Используйте, когда пользователь ищет конкретную игрушку (например «зайка», «мишка», «цветок»). Возвращает совпавшие мастер-классы с ценой в рублях, рейтингом и ссылкой на страницу.',
    inputSchema: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description:
            'Поисковая фраза на русском: название игрушки или тег, например «зайка» или «новогодние»',
          minLength: 1,
        },
      },
      required: ['query'],
      additionalProperties: false,
    },
    outputSchema: {
      type: 'object',
      properties: {
        results: {
          type: 'array',
          description: 'Найденные мастер-классы',
          items: MC_SUMMARY_OUTPUT,
        },
      },
      required: ['results'],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: 'get_masterclass',
    description:
      'Получить подробности одного мастер-класса по его слагу (wordId): описание, цену в рублях, список материалов, рейтинг и число отзывов. Используйте после search_masterclasses или list_masterclasses, когда нужны детали конкретного мастер-класса.',
    inputSchema: {
      type: 'object',
      properties: {
        wordId: {
          type: 'string',
          description:
            'Слаг мастер-класса из поля wordId результатов поиска, например «kotik-i-zayka»',
          minLength: 1,
        },
      },
      required: ['wordId'],
      additionalProperties: false,
    },
    outputSchema: {
      type: 'object',
      properties: {
        ...MC_SUMMARY_OUTPUT.properties,
        description: {
          type: 'string',
          description: 'Текстовое описание мастер-класса',
        },
        materials: {
          type: 'string',
          description: 'Необходимые материалы и пряжа',
        },
      },
      required: ['wordId', 'name', 'priceRub', 'url'],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: 'list_masterclasses',
    description:
      'Полный список всех доступных мастер-классов по вязанию игрушек амигуруми с ценами в рублях, рейтингом и ссылками. Используйте, когда пользователь просит показать весь каталог или порекомендовать игрушку для вязания крючком.',
    inputSchema: {
      type: 'object',
      properties: {},
      additionalProperties: false,
    },
    outputSchema: {
      type: 'object',
      properties: {
        results: {
          type: 'array',
          description: 'Все мастер-классы каталога',
          items: MC_SUMMARY_OUTPUT,
        },
      },
      required: ['results'],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
] as const;

/** JSON-манифест для статичной выдачи в HTML */
export const WEBMCP_MANIFEST = {
  version: '1.0',
  name: 'Марина Камбур — мастер-классы по вязанию игрушек амигуруми',
  description:
    'Инструменты каталога мастер-классов по вязанию игрушек амигуруми крючком: поиск, список и детали мастер-классов. Все инструменты только читают данные.',
  tools: WEBMCP_TOOL_DEFINITIONS,
};
